import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { C, t } from '../../../shared/constants/dashboardConstants';
import { useAuth } from '../../../contexts/AuthContext';
import { changeMyPassword } from '../../../api/authApi';

const MIN_LENGTH = 8;

/**
 * 최초 로그인 시 임시 비밀번호를 바꾸는 화면.
 *
 * PrivateRoute가 mustChangePassword=true인 동안 다른 경로를 전부 이 화면으로 돌리므로,
 * 여기서는 사이드바 없이 단독으로 렌더한다.
 */
export default function ForcePasswordChangePage() {
  const navigate = useNavigate();
  const { setMustChangePassword, refreshSession } = useAuth();
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const tooShort = pw.length > 0 && pw.length < MIN_LENGTH;
  const mismatch = confirm.length > 0 && pw !== confirm;
  const canSubmit = pw.length >= MIN_LENGTH && pw === confirm && !saving;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    setError(null);
    try {
      // 임시 비밀번호 상태에서는 현재 비밀번호를 요구하지 않는다
      await changeMyPassword({ newPassword: pw });
      // 화면에서 혼자 false로 두면 서버가 아직 true일 때 그 사실을 모른 채 통과한다.
      // 세션을 다시 읽어 서버 상태를 따른다. 실패하면 최소한 이 화면은 벗어나게 한다.
      try {
        await refreshSession();
      } catch {
        setMustChangePassword(false);
      }
      navigate('/producer/projects', { replace: true });
    } catch (err) {
      const status = err.response?.status;
      setError(
        status === 400
          ? (err.response?.data?.message ?? '사용할 수 없는 비밀번호입니다.')
          : `변경에 실패했습니다. (HTTP ${status ?? '네트워크'})`
      );
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    width: '100%', height: '52px', padding: '0 16px', boxSizing: 'border-box',
    border: `1px solid ${C.g200}`, borderRadius: '12px',
    fontFamily: 'Pretendard, sans-serif', fontSize: '15px', outline: 'none',
  };

  return (
    <div style={{
      minHeight: '100vh', background: C.bg,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px',
    }}>
      <form
        onSubmit={handleSubmit}
        style={{
          width: '100%', maxWidth: '440px', background: C.white,
          border: `1px solid ${C.g200}`, borderRadius: '20px', padding: '36px 32px',
          display: 'flex', flexDirection: 'column', gap: '20px', boxSizing: 'border-box',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <h1 style={{ ...t('24px', 700, C.bk), margin: 0 }}>비밀번호를 변경해주세요</h1>
          <p style={{ ...t('14px', 400, C.sub), margin: 0, lineHeight: 1.6 }}>
            전달받은 임시 비밀번호는 처음 한 번만 사용할 수 있습니다.<br />
            새 비밀번호를 설정해야 서비스를 이용할 수 있습니다.
          </p>
        </div>

        <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <span style={t('13px', 600, C.sub)}>새 비밀번호</span>
          <input
            type="password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            autoComplete="new-password"
            placeholder={`${MIN_LENGTH}자 이상`}
            style={inputStyle}
          />
          {tooShort && (
            <span style={t('12px', 500, '#EF4444')}>{MIN_LENGTH}자 이상 입력해주세요.</span>
          )}
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <span style={t('13px', 600, C.sub)}>새 비밀번호 확인</span>
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            style={inputStyle}
          />
          {mismatch && (
            <span style={t('12px', 500, '#EF4444')}>비밀번호가 일치하지 않습니다.</span>
          )}
        </label>

        {error && <span style={t('13px', 500, '#EF4444')}>{error}</span>}

        <button
          type="submit"
          disabled={!canSubmit}
          style={{
            height: '52px', borderRadius: '12px', border: 'none',
            background: canSubmit ? C.bk : C.g200,
            cursor: canSubmit ? 'pointer' : 'default',
            ...t('15px', 600, C.white),
          }}
        >
          {saving ? '변경 중...' : '비밀번호 변경'}
        </button>
      </form>
    </div>
  );
}
